import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Lock } from 'lucide-react';
import { Mascot } from '@/components/Mascot';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { useDofiStart } from '@/hooks/useDofiStart';
import { buildReview, REVIEW_FREE, type MealLite } from '@/lib/dofiStart';
import { isPaidNow } from '@/lib/promo';

/**
 * Разбор трёх дней «Старта с Дофи» (30.10.2026). Награда за старт и главный
 * момент продажи: два вывода открыты всем, остальные — размыты с замком
 * и подписью, что именно там. Человек видит, ЧТО он теряет, а не абстрактную
 * подписку. Считает код по отметкам и дневнику, модели нет.
 */
export default function StartReview() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { profile } = useUserStore();
  const { startAt, marks, loaded } = useDofiStart();
  const [meals, setMeals] = useState<MealLite[] | null>(null);

  useEffect(() => {
    if (!profile?.id || !startAt) return;
    const from = new Date(startAt); from.setDate(from.getDate() - 8);
    supabase.from('meals').select('consumed_at,total_calories').eq('user_id', profile.id)
      .gte('consumed_at', from.toISOString())
      .then(({ data }) => setMeals((data as MealLite[]) ?? []));
  }, [profile?.id, startAt]);

  if (!profile) return null;
  const ep = profile.eating_profile;
  const paid = isPaidNow(profile);
  const ready = loaded && meals !== null;
  const lines = ready && startAt && ep ? buildReview(ep.style, startAt, marks, meals!) : [];

  return (
    <div className="max-w-md mx-auto px-5 pt-4 pb-28">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-ink-muted mb-4">
        <ChevronLeft size={18} /> Назад
      </button>
      <Mascot pose="celebrate" className="w-28 h-28 mx-auto mb-3" />
      <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-glow text-center mb-2">Старт с Дофи</div>
      <h1 className="font-display text-2xl font-bold text-center text-balance mb-5">Что изменилось за три дня</h1>

      {!startAt ? (
        <p className="text-sm text-ink-muted text-center">Разбор появится после трёх дней с Дофи.</p>
      ) : !ready ? (
        <p className="text-sm text-ink-muted text-center">Собираю…</p>
      ) : (
        <div className="space-y-3 mb-6">
          {lines.map((l, i) => {
            const locked = !paid && i >= REVIEW_FREE;
            return (
              <div key={i} className="card p-4 flex gap-3 relative overflow-hidden">
                <span className="text-xl shrink-0">{l.emoji}</span>
                <p className={`text-sm leading-relaxed text-ink ${locked ? 'blur-[5px] select-none' : ''}`} aria-hidden={locked}>
                  {l.text}
                </p>
                {locked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex items-center gap-1.5 text-xs text-ink bg-bg-card/90 px-3 py-1.5 rounded-full border border-line">
                      <Lock size={12} /> Откроется, когда продолжишь
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {ready && !paid && lines.length > REVIEW_FREE && (
        <div className="rounded-2xl border border-emerald-glow/30 bg-emerald-glow/5 p-4 mb-4">
          <p className="text-sm text-ink leading-relaxed mb-3">
            Ещё {lines.length - REVIEW_FREE} {lines.length - REVIEW_FREE === 1 ? 'наблюдение' : 'наблюдения'}: как изменился твой день и вечер и какой шаг следующий. Всё, что ты собрал, сохранено.
          </p>
          <button
            onClick={() => { haptic('medium'); nav('/paywall'); }}
            className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold active:scale-[0.98] transition-all"
          >
            Продолжить с Дофи
          </button>
        </div>
      )}
      {ready && paid && (
        <button onClick={() => nav('/chat')}
          className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold active:scale-[0.98] transition-all">
          Обсудить с Дофи
        </button>
      )}
    </div>
  );
}
